import type { DetectedFace } from "./auto-reframe-engine";

/**
 * Last-resort subject detection, kept only for the case where the MediaPipe
 * models cannot load at all. It is a fixed RGB threshold, so it is unreliable
 * across skin tones and blind to any subject that is not a person. Prefer the
 * face and object detectors whenever they are available.
 */
export interface ImageDataLike {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

const BLOCK_SIZE = 8;
const MIN_REGION_BLOCKS = 10;
const REGION_CONFIDENCE = 0.7;
const CENTER_FALLBACK_CONFIDENCE = 0.3;

interface Region {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function detectSkinToneSubjects(image: ImageDataLike): DetectedFace[] {
  const { width, height } = image;
  if (width <= 0 || height <= 0) return [];

  const regions = findConnectedRegions(buildSkinMap(image), width, height);

  return regions
    .filter((r) => r.width > width * 0.05 && r.height > height * 0.05)
    .filter((r) => {
      const aspectRatio = r.width / r.height;
      return aspectRatio > 0.5 && aspectRatio < 2;
    })
    .slice(0, 3)
    .map((r) => ({ ...r, confidence: REGION_CONFIDENCE }));
}

/**
 * The guess used when even the skin-tone pass finds nothing: a person-shaped
 * box in the upper middle of the frame. Framing against it reproduces the
 * headroom of a real detection instead of dead-centring the crop.
 */
export function centerFramedFallbackSubject(
  width: number,
  height: number,
): DetectedFace {
  return {
    x: width * 0.3,
    y: height * 0.2,
    width: width * 0.4,
    height: height * 0.5,
    confidence: CENTER_FALLBACK_CONFIDENCE,
  };
}

function buildSkinMap(image: ImageDataLike): Uint8Array {
  const { data, width, height } = image;
  const skinMap = new Uint8Array(width * height);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      if (isSkinColor(data[idx], data[idx + 1], data[idx + 2])) {
        skinMap[y * width + x] = 1;
      }
    }
  }

  return skinMap;
}

function isSkinColor(r: number, g: number, b: number): boolean {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  if (r <= 95 || g <= 40 || b <= 20) return false;
  if (max - min <= 15) return false;
  if (Math.abs(r - g) <= 15) return false;
  if (r <= g || r <= b) return false;
  if (r > 220 && g > 210 && b > 170) return false;

  return true;
}

function findConnectedRegions(
  skinMap: Uint8Array,
  width: number,
  height: number,
): Region[] {
  const visited = new Uint8Array(width * height);
  const regions: Region[] = [];

  for (let by = 0; by < height; by += BLOCK_SIZE) {
    for (let bx = 0; bx < width; bx += BLOCK_SIZE) {
      const idx = by * width + bx;
      if (skinMap[idx] && !visited[idx]) {
        const region = floodFillRegion(skinMap, visited, width, height, bx, by);
        if (region) {
          regions.push(region);
        }
      }
    }
  }

  return regions;
}

function floodFillRegion(
  skinMap: Uint8Array,
  visited: Uint8Array,
  width: number,
  height: number,
  startX: number,
  startY: number,
): Region | null {
  let minX = startX;
  let maxX = startX;
  let minY = startY;
  let maxY = startY;
  let blockCount = 0;

  const stack: Array<[number, number]> = [[startX, startY]];

  while (stack.length > 0) {
    const [x, y] = stack.pop()!;

    if (x < 0 || x >= width || y < 0 || y >= height) continue;
    const idx = y * width + x;
    if (visited[idx] || !skinMap[idx]) continue;

    visited[idx] = 1;
    blockCount++;

    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);

    stack.push([x + BLOCK_SIZE, y]);
    stack.push([x - BLOCK_SIZE, y]);
    stack.push([x, y + BLOCK_SIZE]);
    stack.push([x, y - BLOCK_SIZE]);
  }

  if (blockCount < MIN_REGION_BLOCKS) return null;

  return {
    x: minX,
    y: minY,
    width: maxX - minX + BLOCK_SIZE,
    height: maxY - minY + BLOCK_SIZE,
  };
}
